import React, { useEffect, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { Transition } from "@headlessui/react";
import * as PiIcons from "react-icons/pi";
import {
  PiXLight,
  PiBellLight,
  PiEyeSlashLight,
  PiCaretUpLight,
} from "react-icons/pi";
import { RootState, useAppDispatch } from "../../store";
import {
  addNotification,
  AppNotification,
  closeSidepanel,
  setNotifications,
  updateNotification as updateNotificationReducer,
} from "./notificationSlice";
import { GetNotifications, updateNotification, markAllNotificationsAsRead } from "../../store/actions/Notifications";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Badge from "../shared/TailwindComponents/Badge";
import Button from "../shared/TailwindComponents/Button";
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, notificationsPanelTourSteps } from '../shared/FeatureTour';

// Mapping of notification types to icons (matching sidebar icons)
const NOTIFICATION_TYPE_ICON_MAP: Record<string, string> = {
  collaborator_added: "PiCheckSquareOffsetLight",
  task_assigned: "PiCheckSquareOffsetLight",
  matter_updated: "PiBriefcaseLight",
  draft_completed: "PiBriefcaseLight",
  team_invite: "PiUsersThreeLight",
  team_feed_post: "PiUsersThreeLight",
  documents_uploaded: "PiBuildingLight",
  default: "PiBellLight",
};

const getNotificationIcon = (notificationType: string) => {
  const iconKey = NOTIFICATION_TYPE_ICON_MAP[notificationType] || NOTIFICATION_TYPE_ICON_MAP.default;
  const IconComponent = PiIcons[iconKey as keyof typeof PiIcons] as any;
  return IconComponent ? (
    <IconComponent className="h-5 w-5 text-gray-600 dark:text-gray-400 shrink-0" />
  ) : (
    <PiBellLight className="h-5 w-5 text-gray-600 dark:text-gray-400 shrink-0" />
  );
};

/**
 * Converts a UTC timestamp string to a human-readable relative time.
 * Falls back to a formatted local date for older notifications.
 */
const formatRelativeTime = (utcDateStr: string): { relative: string; full: string } => {
  // Ensure we parse as UTC - append 'Z' if the string doesn't already indicate a timezone
  const normalized = utcDateStr.endsWith('Z') || utcDateStr.includes('+') ? utcDateStr : `${utcDateStr}Z`;
  const date = new Date(normalized);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  const full = date.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });

  let relative: string;
  if (diffSec < 60) {
    relative = 'Just now';
  } else if (diffMin < 60) {
    relative = `${diffMin}m ago`;
  } else if (diffHr < 24) {
    relative = `${diffHr}h ago`;
  } else if (diffDays === 1) {
    relative = 'Yesterday';
  } else if (diffDays < 7) {
    relative = `${diffDays}d ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    relative = `${weeks}w ago`;
  } else {
    relative = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  return { relative, full };
};
 
const NotificationsSidepanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useSelector((state: RootState) => state.notifications.isOpen);
  const history = useHistory();
  const notifications = useSelector(
    (state: RootState) => state.notifications.notifications
  ) as AppNotification[];
  const unreadCount = useSelector(
    (state: RootState) => state.notifications.unreadCount
  );
  const isSidebarOpen = useSelector((state: RootState) => state.sidebar.isOpen);
  const { t } = useTranslation()
  const sidebarWidth = isSidebarOpen ? 208 : 56;

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'notifications-panel',
    steps: notificationsPanelTourSteps,
    autoStartDelay: 800,
    readyToShow: isOpen,
  });
 
  const unreadNotifications = notifications.filter((n) => !n.is_read);
  const readNotifications = notifications.filter((n) => n.is_read);

  /**
   * READ IS A FOOTER (notif-1).
   *
   * Read notifications used to hold a fixed bottom third of the panel whether
   * or not anyone wanted them, which is a third of the panel spent on what has
   * already been dealt with. They are now a slim footer that expands upward
   * over the list and collapses back down to its bar, and the choice is
   * remembered - reopening the bell should not undo it.
   */
  const READ_OPEN_KEY = 'pons:notifications:readExpanded';
  const [readExpanded, setReadExpanded] = useState<boolean>(() => {
    try {
      return localStorage.getItem(READ_OPEN_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggleRead = () => {
    setReadExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(READ_OPEN_KEY, String(next));
      } catch {
        /* private mode - the panel still works, it just forgets */
      }
      return next;
    });
  };
 
  const fetchNotifications = useCallback(async () => {
    try {
      const response: any = await dispatch(GetNotifications());
      
      if (response?.data?.length > 0) {
        dispatch(setNotifications(response.data.notifications));
      }
    } catch (error) {
      console.error("Error fetching notifications", error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);
 
  const receiveNewNotification = useCallback(
    (data: AppNotification) => {
      dispatch(addNotification(data));
    },
    [dispatch]
  );
 
  const handleNotificationClick = async (notification: AppNotification) => {
   

    // Mark as read when clicked (even if already read)
    try {

      // Call the update notification action
      await dispatch(updateNotification(notification.notification_id) as any);

      // The reducer has already updated the state, so just log success
    } catch (error: any) {
      console.error("[NotificationsSidepanel] Error marking notification as read:", {
        error: error?.message || error,
        notification_id: notification.notification_id
      });
    }

    // Navigate to link if available
    if (notification.link) {
      history.push(notification.link);
    }

    // Close the panel
    dispatch(closeSidepanel());
  };
 
  const handleMarkAllAsRead = async () => {
    try {
      await dispatch(markAllNotificationsAsRead() as any);
    } catch (error) {
      console.error("[NotificationsSidepanel] Error marking all as read:", error);
    }
  };
 
  return (
    <>
      <Transition
        show={isOpen}
        enter="transition ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className="fixed inset-0 bg-ink/20 backdrop-blur-[2px] z-40"
          style={{
            insetInlineStart: `${sidebarWidth}px`,
            width: `calc(100% - ${sidebarWidth}px)`,
          }}
          onClick={() => dispatch(closeSidepanel())}
        />
      </Transition>
 
      <Transition
        show={isOpen}
        enter="transition ease-out duration-300"
        enterFrom="opacity-0 translate-x-[-100%] rtl:translate-x-[100%]"
        enterTo="opacity-100 translate-x-0"
        leave="transition ease-in duration-200"
        leaveFrom="opacity-100 translate-x-0"
        leaveTo="opacity-0 translate-x-[-100%] rtl:translate-x-[100%]"
      >
        <div
          className="glass fixed top-3 w-[480px] shadow-2xl z-50 h-[calc(100vh-24px)] rounded-xl flex flex-col overflow-hidden"
          style={{ insetInlineStart: `${sidebarWidth + 8}px` }}
        >
          <div data-tour-id="notifications-panel" className="p-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-base font-medium text-gray-900 dark:text-gray-100">
              {t('Notifications')}
            </h2>
            <Button
              onClick={() => dispatch(closeSidepanel())}
              type="button"
              variant="outline"
              size="small"
            >
              {t("Close")}
            </Button>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-950">
            <section data-tour-id="notifications-unread" className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main p-3.5">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    {t(`Unread`)}
                  </h3>
                  {unreadCount > 0 && (
                    <Badge color="blue" className="text-xs px-1.5 py-0">
                      {unreadCount}
                    </Badge>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-xs px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition font-medium"
                  >
                    {t('Mark all as read')}
                  </button>
                )}
              </div>
              {unreadNotifications.length === 0 ? (
                <div className="flex flex-col justify-center items-center py-8 gap-2 text-center">
                  <PiBellLight className="text-gray-300 dark:text-gray-600" size={32} />
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {t('No unread notifications')}
                  </p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {unreadNotifications.map((n) => (
                    <li
                      key={n.notification_id}
                      className="p-3 rounded-lg bg-info-50/60 dark:bg-info-900/10 shadow-sm border border-info-200 dark:border-info-800/40 hover:shadow-md hover:border-info-300 dark:hover:border-info-600 cursor-pointer transition-all"
                      onClick={() => handleNotificationClick(n)}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="relative shrink-0">
                          {getNotificationIcon(n.type || "")}
                          <span className="absolute -top-0.5 -end-0.5 h-2 w-2 rounded-full bg-info-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="text-xs text-gray-900 dark:text-gray-100 font-medium leading-snug flex-1">
                              {n.title || n.content}
                            </p>
                            {(() => { const t = formatRelativeTime(n.created_at); return (
                              <span className="text-[10px] text-info-600 dark:text-info-400 whitespace-nowrap flex-shrink-0 font-medium" title={t.full}>
                                {t.relative}
                              </span>
                            ); })()}
                          </div>
                          {n.title && n.content !== n.title && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 w-11/12">
                              {n.content}
                            </p>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section
              data-tour-id="notifications-read"
              className={`glass-bar flex flex-col flex-none border-t border-hairline transition-[height] duration-300 ease-slide overflow-hidden ${
                readExpanded ? 'h-2/3' : 'h-11'
              }`}
            >
              <button
                type="button"
                onClick={toggleRead}
                aria-expanded={readExpanded}
                title={readExpanded ? t('Hide read notifications') : t('Show read notifications')}
                className="flex h-11 flex-none items-center gap-2 px-3.5 text-start transition-colors hover:bg-surface-sunken/60"
              >
                <PiCaretUpLight
                  className={`h-4 w-4 text-fg-muted transition-transform duration-300 ease-slide ${
                    readExpanded ? 'rotate-180' : ''
                  }`}
                />
                <h3 className="flex-1 text-xs font-medium text-fg-body">{t('Read')}</h3>
                {readNotifications.length > 0 && (
                  <Badge color="gray" className="text-xs px-1.5 py-0">
                    {readNotifications.length}
                  </Badge>
                )}
              </button>

              <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar-main px-3.5 pb-3.5">
              {readNotifications.length === 0 ? (
                <div className="flex flex-col justify-center items-center py-6 gap-2 text-center">
                  <PiEyeSlashLight className="text-gray-300 dark:text-gray-600" size={28} />
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {t('No read notifications')}
                  </p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {readNotifications.map((n) => (
                    <li
                      key={n.notification_id}
                      className="glass p-3 rounded-lg hover:shadow-md hover: dark:hover: cursor-pointer transition-all opacity-70 hover:opacity-100"
                      onClick={() => handleNotificationClick(n)}
                    >
                      <div className="flex items-start gap-2.5">
                        {getNotificationIcon(n.type || "")}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="text-xs text-gray-800 dark:text-gray-300 font-medium leading-snug flex-1">
                              {n.title || n.content}
                            </p>
                            {(() => { const t = formatRelativeTime(n.created_at); return (
                              <span className="text-[10px] text-gray-500 dark:text-gray-500 whitespace-nowrap flex-shrink-0" title={t.full}>
                                {t.relative}
                              </span>
                            ); })()}
                          </div>
                          {n.title && n.content !== n.title && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1 w-11/12">
                              {n.content}
                            </p>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              </div>
            </section>
          </div>
        </div>
      </Transition>
      <FeatureTour
        steps={notificationsPanelTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </>
  );
};
 
export default NotificationsSidepanel;
